import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

const LandingPage: React.FC = () => {
    const observerRef = useRef<IntersectionObserver | null>(null);

    useEffect(() => {
        observerRef.current = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible');
                }
            });
        }, { threshold: 0.1 });

        const hiddenElements = document.querySelectorAll('.animate-on-scroll');
        hiddenElements.forEach((el) => observerRef.current?.observe(el));

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    return (
        <div className="landing-page">
            <nav className="landing-nav">
                <div className="logo">RK Warranty</div>

                <div className="nav-contact-info">
                    <a href="tel:+917618019648" className="contact-btn" title="Call Support">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                    </a>
                    <a href="mailto:Combsaf107am@gmail.com" className="contact-btn" title="Email Support">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                    </a>
                </div>

                <div className="nav-links">
                    <Link to="/login" className="btn btn-outline">Login</Link>
                    <Link to="/register" className="btn btn-primary">Sign Up</Link>
                </div>
            </nav>

            <header className="hero-section animate-on-scroll">
                <div className="hero-content">
                    <h1 className="hero-title">Premium Warranty <br /> Protection <span className="highlight">Redefined</span></h1>
                    <p className="hero-subtitle">Secure your devices with our trusted warranty services. Fast approvals, seamless claims, and complete peace of mind.</p>
                    <div className="hero-buttons">
                        <Link to="/register" className="btn btn-lg btn-primary">Get Started</Link>
                        <Link to="/login" className="btn btn-lg btn-outline">Login</Link>
                    </div>
                </div>
                <div className="hero-glow"></div>
            </header>

            <section className="features-section">
                <div className="container">
                    <h2 className="section-title animate-on-scroll">Why Choose Us?</h2>
                    <div className="features-grid">
                        <div className="feature-card animate-on-scroll">
                            <div className="feature-icon">⚡</div>
                            <h3>Instant Activation</h3>
                            <p>Get your warranty active immediately after registration without any hassle.</p>
                        </div>
                        <div className="feature-card animate-on-scroll delay-100">
                            <div className="feature-icon">🛡️</div>
                            <h3>Comprehensive Cover</h3>
                            <p>We cover display issues that matter most: lines, color shifts, and touch failures.</p>
                        </div>
                        <div className="feature-card animate-on-scroll delay-200">
                            <div className="feature-icon">🎧</div>
                            <h3>Expert Support</h3>
                            <p>Our dedicated team is ready to assist you with claims and queries 24/7.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="how-it-works-section">
                <div className="container">
                    <h2 className="section-title animate-on-scroll">How It Works</h2>
                    <div className="steps-container">
                        <div className="step-item animate-on-scroll">
                            <div className="step-number">1</div>
                            <div className="step-content">
                                <h3>Register</h3>
                                <p>Create your account and add your device details.</p>
                            </div>
                        </div>
                        <div className="step-line animate-on-scroll"></div>
                        <div className="step-item animate-on-scroll delay-100">
                            <div className="step-number">2</div>
                            <div className="step-content">
                                <h3>Activate</h3>
                                <p>Submit your warranty request for instant approval.</p>
                            </div>
                        </div>
                        <div className="step-line animate-on-scroll"></div>
                        <div className="step-item animate-on-scroll delay-200">
                            <div className="step-number">3</div>
                            <div className="step-content">
                                <h3>Claim</h3>
                                <p>Face an issue? File a claim and get it resolved fast.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="warranty-section animate-on-scroll">
                <div className="container">
                    <h2 className="section-title">Warranty <span className="text-gold">Policy</span></h2>
                    <div className="warranty-card">
                        <div className="warranty-header">
                            <h3>Important Coverage Information</h3>
                            <span className="badge">No Manufacturer Warranty</span>
                        </div>
                        <div className="warranty-body">
                            <p className="warranty-intro">The warranty is valid <strong>only</strong> for the following issues:</p>
                            <ul className="warranty-list">
                                <li>
                                    <span className="icon">⚪</span>
                                    <span>White Display</span>
                                </li>
                                <li>
                                    <span className="icon">🔵</span>
                                    <span>Blue Display</span>
                                </li>
                                <li>
                                    <span className="icon">👆</span>
                                    <span>Touch Not Working</span>
                                </li>
                                <li>
                                    <span className="icon">➖</span>
                                    <span>Display Lines</span>
                                </li>
                                <li>
                                    <span className="icon">🔆</span>
                                    <span>Blinking Display</span>
                                </li>
                            </ul>
                            <div className="warranty-alert">
                                <span className="alert-icon">⚠️</span>
                                <p>If <strong>3 consecutive claims</strong> are made on the same ID, the ID will be blocked.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <footer className="landing-footer">
                <div className="container">
                    <div className="footer-content">
                        <div className="footer-brand">
                            <h3>RK Warranty</h3>
                            <p>Premium protection for your digital life.</p>
                        </div>
                        <div className="footer-links">
                            <a href="#">Privacy Policy</a>
                            <a href="#">Terms of Service</a>
                            <a href="#">Contact Support</a>
                        </div>
                    </div>
                    <div className="footer-bottom">
                        <p>&copy; {new Date().getFullYear()} RK Warranty. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
